import styled from '@emotion/styled';
import type { Column, Row } from '@innovamat/glimmer-components';
import {
  TableComponent,
  Typography,
  useGlimmerTheme,
} from '@innovamat/glimmer-components';
import { useTranslation } from 'react-i18next';
import { renderScore, renderTime } from '../../utils/render-rows';
import { getReportsTypography } from '../../utils/typography';

export function StudentDrawerDigitalPractice(row: Row): JSX.Element {
  const theme = useGlimmerTheme();
  const { t } = useTranslation();

  const renderApplets = (applets: number): JSX.Element =>
    applets !== 0 ? (
      <NumberOfApplets>
        {applets}
        <Suffix>{t('reports.appletsDone.cell')}</Suffix>
      </NumberOfApplets>
    ) : (
      <NoApplets>-</NoApplets>
    );

  const digitalPracticeColumns: Column<Row>[] = [
    {
      id: 'appletsDone',
      label: t('reports.appletsDone.title.column'),
      width: 100,
      align: 'left',
      render: (_, row) => renderApplets(row?.appletsCompleted),
    },
    {
      id: 'separator-2',
      width: 9,
      separator: true,
    },
    {
      id: 'timeSpent',
      label: t('reports.time.title.column'),
      width: 100,
      align: 'center',
      render: (_, row) => row && renderTime(row, 'all', 'generalProgress'),
    },
    {
      id: 'separator-2',
      width: 9,
      separator: true,
    },
    {
      id: 'averageScore',
      label: t('reports.score.course.title.column'),
      width: 100,
      align: 'left',
      render: (_, row) => renderScore(row?.averageScore),
    },
  ];

  return (
    <TableContainer>
      <TableComponent
        columns={digitalPracticeColumns}
        rows={[row.row]}
        id="student-drawer-digital-practice"
        backgroundColor={
          theme.tokens.color.specific.reports.tables['background-highlight']
            .value
        }
      />
    </TableContainer>
  );
}

const TableContainer = styled.div`
  max-width: 496px;
  min-width: 0;
  background-color: ${({ theme }) =>
    theme.tokens.color.specific.reports.tables['background-highlight'].value};
  border-radius: 8px;
  padding: 8px 16px 8px 16px;
  .separator-wrapper {
    background-color: ${({ theme }) =>
      theme.tokens.color.alias.cm.surface['surface-secondary'].value};
  }
  td {
    div {
      justify-content: center;
    }
    .muiTableCell-root {
      border-bottom: 0;
    }
  }
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    max-width: 100%;
  }
`;

export const NumberOfApplets = styled.div`
  color: ${({ theme }) => theme.tokens.color.alias.cm.text['text'].value};
  ${getReportsTypography()}
  flex: 0 0 50%;
  display: flex;
  align-items: flex-end;
  justify-content: center;
`;

export const NoApplets = styled(NumberOfApplets)`
  color: ${({ theme }) =>
    theme.tokens.color.specific.reports.semantic['no-data'].value};
  align-items: center;
`;

export const Suffix = styled(Typography.Subtitle2)`
  display: inline;
  padding-left: 2px;
`;
